import { useEffect, useRef, useCallback } from 'react';

// Performance monitoring hook
export const usePerformanceMonitor = (componentName) => {
  const renderCount = useRef(0);
  const lastRenderTime = useRef(Date.now());
  const mountTime = useRef(Date.now());

  useEffect(() => {
    renderCount.current += 1;
    const now = Date.now();
    const timeSinceLastRender = now - lastRenderTime.current;
    lastRenderTime.current = now;

    // Log performance metrics in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[${componentName}] Render #${renderCount.current}, Time since last render: ${timeSinceLastRender}ms`);
    }

    // Report slow renders (>16ms for 60fps)
    if (timeSinceLastRender > 16) {
      console.warn(`[${componentName}] Slow render detected: ${timeSinceLastRender}ms`);
    }
  });

  const measureAsync = useCallback(async (operationName, asyncOperation) => {
    const start = performance.now();
    try {
      const result = await asyncOperation();
      const end = performance.now();
      const duration = end - start;
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`[${componentName}] ${operationName}: ${duration.toFixed(2)}ms`);
      }
      
      return result;
    } catch (error) {
      const end = performance.now();
      const duration = end - start;
      console.error(`[${componentName}] ${operationName} failed after ${duration.toFixed(2)}ms:`, error);
      throw error;
    }
  }, [componentName]);

  const getStats = useCallback(() => ({
    renderCount: renderCount.current,
    mountTime: mountTime.current,
    timeSinceMount: Date.now() - mountTime.current
  }), []);

  return { measureAsync, getStats };
};

// Memory usage monitor
export const useMemoryMonitor = () => {
  const checkMemory = useCallback(() => {
    if ('memory' in performance) {
      const memory = performance.memory;
      return {
        used: Math.round(memory.usedJSHeapSize / 1048576), // MB
        total: Math.round(memory.totalJSHeapSize / 1048576), // MB
        limit: Math.round(memory.jsHeapSizeLimit / 1048576) // MB
      };
    }
    return null;
  }, []);

  const logMemoryUsage = useCallback(() => {
    const memory = checkMemory();
    if (memory && process.env.NODE_ENV === 'development') {
      console.log(`Memory Usage: ${memory.used}MB / ${memory.total}MB (Limit: ${memory.limit}MB)`);
    }
  }, [checkMemory]);

  return { checkMemory, logMemoryUsage };
};

// Bundle size analyzer helper
export const getBundleInfo = () => {
  if (process.env.NODE_ENV === 'development') {
    // This would typically be populated by webpack-bundle-analyzer
    return {
      totalSize: 'Check webpack-bundle-analyzer for details',
      chunkSizes: 'Run npm run analyze to see bundle breakdown'
    };
  }
  return null;
};
